import type { AdminEventProposalDto, ProposalStatus } from '@bravo/shared';
import type { ColumnDef } from '@tanstack/react-table';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { DataTable } from '../components/DataTable';
import {
  Badge,
  Button,
  Empty,
  ErrorBlock,
  Field,
  PageHeader,
  Spinner,
  TextArea,
  statusTone,
} from '../components/ui';
import { apiData, apiPage } from '../lib/api';
import { toastError, toastOk } from '../lib/feedback';
import { useFormat } from '../lib/format';

const STATUSES: ProposalStatus[] = ['pending', 'approved', 'rejected'];

export function ProposalsPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ProposalStatus>('pending');
  const [selected, setSelected] = useState<AdminEventProposalDto | null>(null);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<'convert' | 'reject' | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['proposals', status],
    queryFn: ({ pageParam }) =>
      apiPage<AdminEventProposalDto>('/admin/proposals', { status, limit: 20, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data]);

  function open(row: AdminEventProposalDto) {
    setSelected(row);
    setNote('');
    setReason('');
  }

  async function convert() {
    if (!selected) return;
    setBusy('convert');
    try {
      const result = await apiData<{ marketId: string }>(
        `/admin/proposals/${selected.id}/convert`,
        {
          method: 'POST',
          body: note.trim() ? { reviewNote: note.trim() } : {},
        },
      );
      toastOk(t('proposals.converted'));
      await queryClient.invalidateQueries({ queryKey: ['proposals'] });
      navigate(`/markets/${result.marketId}`);
    } catch (error) {
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(null);
    }
  }

  async function reject() {
    if (!selected) return;
    setBusy('reject');
    try {
      await apiData(`/admin/proposals/${selected.id}/reject`, {
        method: 'POST',
        body: { reason: reason.trim() },
      });
      toastOk(t('proposals.reject'));
      setSelected(null);
      await queryClient.invalidateQueries({ queryKey: ['proposals'] });
    } catch (error) {
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(null);
    }
  }

  const columns: ColumnDef<AdminEventProposalDto>[] = [
    {
      id: 'user',
      header: t('user'),
      cell: ({ row }) => row.original.userName || row.original.userPhone || row.original.userId,
    },
    {
      id: 'question',
      header: t('proposals.question'),
      cell: ({ row }) => row.original.title.en || row.original.title.am,
    },
    {
      id: 'category',
      header: t('markets.category'),
      cell: ({ row }) => format.category(row.original.category),
    },
    {
      id: 'when',
      header: t('proposals.when'),
      cell: ({ row }) => format.when(row.original.suggestedResolutionDate),
    },
    {
      id: 'status',
      header: t('status'),
      cell: ({ row }) => (
        <Badge tone={statusTone(row.original.status)}>{format.status(row.original.status)}</Badge>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title={t('proposals.title')} subtitle={t('proposals.subtitle')} />
      <div className="mb-4 flex gap-2">
        {STATUSES.map((item) => (
          <Button
            key={item}
            variant={item === status ? 'primary' : 'secondary'}
            onClick={() => {
              setStatus(item);
              setSelected(null);
            }}
          >
            {format.status(item)}
          </Button>
        ))}
      </div>
      {query.isLoading ? <Spinner /> : null}
      {query.isError ? (
        <ErrorBlock message={t('errorTitle')} onRetry={() => void query.refetch()} />
      ) : null}
      {!query.isLoading && items.length === 0 ? <Empty title={t('proposals.empty')} /> : null}
      {items.length > 0 ? <DataTable columns={columns} data={items} onRow={open} /> : null}
      {query.hasNextPage ? (
        <Button className="mt-4" variant="secondary" onClick={() => void query.fetchNextPage()}>
          {t('loadMore')}
        </Button>
      ) : null}

      {selected ? (
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <h2 className="text-lg font-semibold">{t('details')}</h2>
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-fg-muted">{t('user')}</dt>
              <dd>{selected.userName || selected.userPhone || selected.userId}</dd>
            </div>
            <div>
              <dt className="text-fg-muted">{t('proposals.question')}</dt>
              <dd>{selected.title.en}</dd>
            </div>
            <div>
              <dt className="text-fg-muted">{t('langAm')}</dt>
              <dd>{selected.title.am || t('proposals.noAmharic')}</dd>
            </div>
            <div>
              <dt className="text-fg-muted">{t('proposals.when')}</dt>
              <dd>{format.when(selected.suggestedResolutionDate)}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-fg-muted">{t('proposals.source')}</dt>
              <dd>
                {selected.resolutionSource}
                {selected.resolutionSourceUrl ? (
                  <>
                    {' '}
                    <a className="text-brand-text underline" href={selected.resolutionSourceUrl}>
                      {selected.resolutionSourceUrl}
                    </a>
                  </>
                ) : null}
              </dd>
            </div>
          </dl>
          {selected.status === 'pending' ? (
            <div className="mt-4 space-y-3">
              <Field label={t('proposals.note')}>
                <TextArea
                  value={note}
                  maxLength={500}
                  onChange={(event) => setNote(event.target.value)}
                />
              </Field>
              <Field label={t('reason')}>
                <TextArea value={reason} onChange={(event) => setReason(event.target.value)} />
              </Field>
              <div className="flex gap-2">
                <Button
                  data-testid="proposal-convert"
                  loading={busy === 'convert'}
                  onClick={() => void convert()}
                >
                  {t('proposals.convert')}
                </Button>
                <Button
                  variant="danger"
                  loading={busy === 'reject'}
                  disabled={reason.trim().length < 3}
                  onClick={() => void reject()}
                >
                  {t('proposals.reject')}
                </Button>
              </div>
            </div>
          ) : null}
          {selected.convertedMarketId ? (
            <p className="mt-4 text-sm">
              <Link
                className="text-brand-text underline"
                to={`/markets/${selected.convertedMarketId}`}
              >
                {t('proposals.openMarket')}
              </Link>
            </p>
          ) : null}
          {selected.reviewNote && selected.status !== 'pending' ? (
            <p className="mt-3 text-sm text-fg-muted">
              {t('reason')}: {selected.reviewNote}
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
